import React, { useState, useCallback, useEffect } from 'react';
import { ReactSortable } from "react-sortablejs";
import Cropper from 'react-easy-crop';
function InputMultiImage({multiImagenes,handleChangeImagen,maxImages,ratio,name,errores}) {
    const MAX_IMAGES = maxImages || 5;
    const [images, setImages] = useState(multiImagenes);
    const [error, setError] = useState('');

    useEffect(() => {
        handleChangeImagen(images);
    }, [images]);
    
    // Estados para el modal de recorte
    const [modalOpen, setModalOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [aspectRatio] = useState(ratio || 5 / 4)

    // Función para manejar el completado del recorte
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Función para crear una imagen a partir de una URL
    const createImage = (url) =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.addEventListener('load', () => resolve(image));
            image.addEventListener('error', (error) => reject(error));
            image.src = url;
        });

    // Función para detectar si una imagen tiene transparencia
    const hasTransparency = (imageData) => {
        const data = imageData.data;
        for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 255) return true; // Si algún píxel tiene canal alpha < 255
        }
        return false;
    };

    // Función para obtener la imagen recortada
    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        // Dibujar la imagen en el canvas
        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        // Verificar si la imagen tiene transparencia
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const transparent = hasTransparency(imageData);
        const mimeType = transparent ? 'image/png' : 'image/jpeg';
        const quality = 0.92; // Calidad para JPEG

        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => resolve(blob),
                mimeType,
                quality
            );
        });
    };


    // Función para completar el recorte y guardar la imagen
    const handleCropSave = async () => {
        try {
            if (!currentImage || !croppedAreaPixels) return;
            
            const croppedBlob = await getCroppedImg(currentImage.preview, croppedAreaPixels);
            // Mantener la extensión original o usar .png para transparencia
            const fileExt = currentImage.file.name.split('.').pop().toLowerCase();
            const isTransparent = fileExt === 'png' || fileExt === 'gif' || fileExt === 'webp';
            const mimeType = isTransparent ? 'image/png' : 'image/jpeg';
            const newFileName = currentImage.file.name.replace(/\.[^/.]+$/, '') + (isTransparent ? '.png' : '.jpg');
            
            const croppedFile = new File([croppedBlob], newFileName, { type: mimeType });
            const croppedPreview = URL.createObjectURL(croppedBlob);
            
            // Si la imagen ya existe en el array, actualízala
            if (images.some(img => img.id === currentImage.id)) {
                setImages(prevImages => prevImages.map((img) => 
                    img.id === currentImage.id  ? { ...img, file: croppedFile, preview: croppedPreview }  : img
                ));
            } else {
                // Si es una imagen nueva, agrégala al array
                const base64 = await toBase64(croppedFile);
                const newImage = {
                    id: currentImage.id,
                    file: croppedFile,
                    preview: base64,
                    esNuevo: true,
                    nombre: currentImage.file.name,
                };
                setImages(prevImages => [...prevImages, newImage]);
            }
            
            // Cerrar el modal
            setModalOpen(false);
            setCurrentImage(null);
        } catch (error) {
            console.error('Error al recortar la imagen:', error);
        }
    };

    // Función para cerrar el modal sin guardar
    const handleCloseModal = () => {
        setModalOpen(false);
        setCurrentImage(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    // Función para convertir un archivo a base64
    const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });

    // Función para manejar la carga de imágenes
    const handleImageUpload = async (event) => {
        // Reset previous error
        setError('');

        const files = Array.from(event.target.files);
        
        // Check if adding new images would exceed the limit
        if (images.length + files.length > MAX_IMAGES) {
            setError(`Solo puedes subir un máximo de ${MAX_IMAGES} imágenes`);
            return;
        }

        try {
            // Si es una sola imagen, abre el modal de recorte
            if (files.length === 1) {
                const file = files[0];
                
                const newImage = {
                    id: `${Date.now()}-${file.name}`,
                    file: file,
                    preview: URL.createObjectURL(file),
                };
                setCurrentImage(newImage);
                setModalOpen(true);
            } else {
                // Si son múltiples imágenes, agrégalas directamente
                const newImages = await Promise.all(files.map(async (file) => {
                    return {
                        id: `${Date.now()}-${file.name}`, 
                        file: file,
                        preview: URL.createObjectURL(file),
                    };
                }));

                setImages(prevImages => [...prevImages, ...newImages]);
            }
        } catch (error) {
            console.error('Error al convertir la imagen a base64:', error);
            setError('Error al procesar las imágenes');
        }
        
        // Limpiar el input de archivo
        event.target.value = '';
    };

    const handleRemoveImage = (id) => {
        setImages(prevImages => prevImages.filter(image => image.id !== id));
        // Clear any previous error when removing an image
        setError('');
    };

    // Limpiar URLs de objetos al desmontar el componente
    React.useEffect(() => {
        return () => {
            images.forEach(image => {
                URL.revokeObjectURL(image.preview);
            });
        };
    }, [images]);

    return (
        <div className="image-upload-container">
            <label
                className="form-label fw-semibold text-primary-light text-sm mb-8 d-flex justify-content-start"
            >
                Imagen <span className="text-danger-600"></span> <span className="text-primary-light ms-2">{images.length}/{MAX_IMAGES}</span>
            </label>
            {images.length < MAX_IMAGES && (
                <div className="input-group">
                    <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageUpload} 
                        title='Subir Imagen'
                        className={`form-control mb-3 ${errores?.[name] ? 'is-invalid' : ''}`}
                    />
                    <div className="invalid-feedback">
                        {errores?.[name]}
                    </div>
                </div>
            )}

            {error && (
                <div className="error-message">{error}</div>
            )}

            {images.length > 0 && (
                <ReactSortable 
                    list={images} 
                    setList={setImages} 
                    className="image-grid"
                >
                    {images.map((image, index) => (
                        <div key={image.id} className="image-item">
                            <img 
                                src={image.preview} 
                                // alt={image.file.name} 
                                className="image-preview"
                            />
                            <p>Posicion: {index + 1}</p>
                            <button 
                                onClick={() => handleRemoveImage(image.id)}
                                className="remove-button"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </ReactSortable>
            )}

            {images.length === MAX_IMAGES && (
                <p className="max-images-message">Límite máximo de imágenes alcanzado</p>
            )}

            {/* Modal para recortar imágenes */}
            {modalOpen && currentImage && (
                <div className="crop-modal-overlay">
                    <div className="crop-modal-content">
                        <h2>Recortar Imagen</h2>
                        <div className="crop-container">
                            <Cropper
                                image={currentImage.preview}
                                crop={crop}
                                zoom={zoom}
                                aspect={aspectRatio}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </div>
                        <div className="crop-controls">
                            <div className="zoom-control">
                                <label style={{color: '#333'}}>Zoom:</label>
                                <input 
                                    type="range" 
                                    value={zoom} 
                                    min={1} 
                                    max={3} 
                                    step={0.1} 
                                    onChange={(e) => setZoom(Number(e.target.value))} 
                                />
                            </div>
                        </div>
                        <div className="crop-modal-buttons">
                            <button onClick={handleCloseModal} className="cancel-button">Cancelar</button>
                            <button onClick={handleCropSave} className="save-button">Guardar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InputMultiImage;
